import axios from 'axios'
import { transientMessage as Message, resetSessionFeedback } from '@/utils/messages'

const { createRefreshCoordinator, sessionChangedError } = require('./authSession.cjs')

export const API_BASE_URL = '/api'

// 把后端各种形态的错误体统一成一句可读文本：
// - FastAPI 校验错误：detail 是 [{loc, msg, type}, ...]
// - 业务错误：detail / message 是字符串
// - 网关/代理：data 是纯文本或 HTML
function pickErrorMessage (data) {
  if (!data) return ''
  if (typeof data === 'string') {
    const text = data.trim()
    if (!text || text.startsWith('<')) return ''
    return text
  }
  const detail = data.detail || data.message
  if (!detail) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map(item => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter(part => part !== 'body').join('.')
            : ''
          return [loc, item.msg].filter(Boolean).join(': ')
        }
        return ''
      })
      .filter(Boolean)
      .join('；')
  }
  if (typeof detail === 'object') return detail.msg || ''
  return ''
}

// access token 有效期 30 分钟，提前 5 分钟主动刷新，确保永不过期
const PROACTIVE_REFRESH_MS = 25 * 60 * 1000

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000
})

// ========== 会话清理 ==========

function clearSession () {
  resetSessionFeedback()
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('username')
  localStorage.removeItem('role')
  localStorage.removeItem('modules')
}

function redirectToLogin () {
  stopProactiveRefresh()
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// ========== 主动刷新调度（防止空闲时 access token 过期断链） ==========

let refreshTimer = null

function startProactiveRefresh () {
  stopProactiveRefresh()
  if (!localStorage.getItem('refreshToken')) return
  refreshTimer = setTimeout(async () => {
    try {
      await refreshCoordinator.run()
    } catch (e) {
      // 主动刷新失败不强制登出，留给 401 拦截器统一处理
      console.warn('主动 token 刷新失败:', e.message)
    }
    // 递归调度下一轮
    startProactiveRefresh()
  }, PROACTIVE_REFRESH_MS)
}

function stopProactiveRefresh () {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

// 页面从隐藏恢复可见时，立即刷新一次后重启周期
function handleVisibilityChange () {
  if (document.visibilityState === 'visible' && localStorage.getItem('refreshToken')) {
    stopProactiveRefresh()
    refreshCoordinator.run()
      .then(() => { startProactiveRefresh() })
      .catch(() => { startProactiveRefresh() })
  }
}

document.addEventListener('visibilitychange', handleVisibilityChange)

// ========== 刷新协调器 ==========

const refreshCoordinator = createRefreshCoordinator({
  sessionVersion: () => Message.sessionVersion(),
  refresh: async () => {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      throw new Error('登录已过期')
    }
    const response = await axios.post(`${API_BASE_URL}/v1/auth/refresh`, {
      refresh_token: refreshToken
    })
    return response.data.data
  },
  save: data => {
    localStorage.setItem('token', data.token)
    localStorage.setItem('refreshToken', data.refresh_token)
    localStorage.setItem('userInfo', JSON.stringify(data.user_info))
    if (data.user_info) {
      localStorage.setItem('username', data.user_info.username || '')
      localStorage.setItem('role', data.user_info.role || '')
      localStorage.setItem('modules', JSON.stringify(data.user_info.modules || []))
    }
    // 每次成功刷新后重设主动刷新定时器
    startProactiveRefresh()
  },
  clear: () => {
    clearSession()
    stopProactiveRefresh()
  }
})

// 页面加载时已有 token 则立即启动主动刷新
if (localStorage.getItem('refreshToken')) {
  startProactiveRefresh()
}

service.interceptors.request.use(
  config => {
    config._feedbackSession = Message.sessionVersion()
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

service.interceptors.response.use(
  response => {
    if (response.config && response.config._feedbackSession !== undefined && !Message.isCurrentSession(response.config._feedbackSession)) {
      return Promise.reject(sessionChangedError())
    }
    const res = response.data
    if (res && typeof res === 'object' && !Object.prototype.hasOwnProperty.call(res, 'code')) {
      return res
    }
    if (res.code !== 200) {
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return res
  },
  async error => {
    const status = error.response && error.response.status
    const originalRequest = error.config || {}
    // A response from the previous account must not flash an error or refresh
    // credentials belonging to the account that has just logged in.
    if (originalRequest._feedbackSession !== undefined && !Message.isCurrentSession(originalRequest._feedbackSession)) {
      return Promise.reject(sessionChangedError())
    }
    const isAuthRequest = String(originalRequest.url || '').includes('/auth/')

    if (status === 401 && !isAuthRequest && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const authData = await refreshCoordinator.run()
        if (!Message.isCurrentSession(originalRequest._feedbackSession)) throw sessionChangedError()
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = `Bearer ${authData.token}`
        return service(originalRequest)
      } catch (refreshError) {
        if (refreshError.code === 'SESSION_CHANGED') return Promise.reject(refreshError)
        Message({ message: '登录已过期，请重新登录', type: 'error', duration: 3000 })
        redirectToLogin()
        return Promise.reject(refreshError)
      }
    }

    const messages = {
      401: '用户名或密码错误',
      403: '没有权限访问',
      404: '请求的资源不存在',
      429: '登录尝试过多，请稍后重试',
      500: '服务器内部错误'
    }
    let message = messages[status] || '网络连接失败，请检查后端服务'
    const detail = pickErrorMessage(error.response && error.response.data)
    if (detail) message = detail
    if (!originalRequest.silentError) Message({ message: String(message), type: 'error', duration: 3000 })
    return Promise.reject(error)
  }
)

export default service
